import { useEffect, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import Card from '@mui/material/Card';
import TableRow from '@mui/material/TableRow';
import { Button, Container, IconButton } from '@mui/material';
import Page from '../../components/Page';
import { useAppSelector, useAppDispatch } from '../../store';
import { setStatus } from '../../store/addonCategory/addonCategorySlice';
import {
  findAddOnCategory,
  createAddOnCategory,
  patchAddOnCategory,
} from '../../store/addonCategory/api';
import Loader from '../../components/Loader';
import Label from '../../components/Label';
import Iconify from '../../components/Iconify';
import { useFormik } from 'formik';
import useToggle from '../../hooks/useToggle';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import Scrollbar from '../../components/Scrollbar';
import Searchbar from '../../components/Searchbar';
import { omit } from 'lodash';
import { reset as resetAddOnCategory } from '../../store/addonCategory/addonCategorySlice';
import {
  reset as resetSelectAddOnCategory,
  setStatus as setSelectAddOnCategoryStatus,
} from '../../store/selectAddOnCategory/selectAddOnCategorySlice';
import AddOnCategoryFormModal from './AddOnCategoryFormModal';
import { AddOnCategory } from '../../store/addonCategory/api/interface';
import addOnCategorySchema from '../../validation/addOnCategorySchema';

const Index: React.FC = () => {
  const dispatch = useAppDispatch();
  const { data, status, isLoading } = useAppSelector(
    (state) => state.addOnCategory
  );
  const [search, setSearch] = useState<string>('');
  const { toggle, onOpen, onClose } = useToggle();

  useEffect(() => {
    if (status === 'idle') {
      dispatch(
        findAddOnCategory({
          ...(search && { search: search }),
        })
      );
    }
  }, [dispatch, status]);

  useEffect(() => {
    dispatch(resetAddOnCategory());
  }, [search]);

  const {
    handleSubmit,
    values,
    setFieldValue,
    resetForm,
    isSubmitting,
    errors,
    touched,
    handleChange,
    setValues,
  } = useFormik({
    initialValues: {
      id: '',
      labelEn: '',
      labelAr: '',
      emoji: '',
      previewUrl: '',
      position: '',
      activeState: true,
    },
    validationSchema: addOnCategorySchema,
    onSubmit: async (values) => {
      try {
        if (values.id) {
          await dispatch(patchAddOnCategory(values)).unwrap();
        }
        if (!values.id) {
          const reqBody = omit(values, ['id']);
          await dispatch(createAddOnCategory(reqBody)).unwrap();
        }
        onClose();
        resetForm();
        dispatch(setStatus({ status: 'idle' }));
        dispatch(resetSelectAddOnCategory());
        dispatch(setSelectAddOnCategoryStatus({ status: 'idle' }));
      } catch (error: any) {}
    },
  });

  const handleUpdate = (value: AddOnCategory): void => {
    onOpen();
    setValues({
      id: value.id,
      labelEn: value?.labelEn,
      labelAr: value?.labelAr,
      emoji: value?.emoji,
      previewUrl: value?.previewUrl,
      position: value?.position,
      activeState: value?.activeState,
    });
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearch(e.target.value);
  };

  return (
    <>
      <Page title={'Add-On Category: List'}>
        <Container maxWidth={'xl'}>
          <HeaderBreadcrumbs
            heading={'Add-On Category'}
            links={[{ name: 'Dashboard', href: '/' }]}
            action={
              <Button
                variant='contained'
                startIcon={<Iconify icon={'eva:plus-fill'} />}
                onClick={onOpen}
              >
                {'New Add-On Category'}
              </Button>
            }
          />

          <Card>
            <Searchbar handleSearch={handleSearch} />

            <Scrollbar>
              <TableContainer sx={{ minWidth: 1000, position: 'relative' }}>
                <Table size={'medium'}>
                  <TableHead>
                    <TableRow>
                      <TableCell align='center'>{'Label EN'}</TableCell>
                      <TableCell align='center'>{'Label AR'}</TableCell>
                      <TableCell align='center'>{'Emoji'}</TableCell>
                      <TableCell align='center'>{'Position'}</TableCell>
                      <TableCell align='center'>{'Status'}</TableCell>
                      <TableCell align='center'>{'Action'}</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {isLoading ? (
                      <TableRow hover>
                        <TableCell
                          colSpan={7}
                          align='center'
                        >
                          <Loader />
                        </TableCell>
                      </TableRow>
                    ) : data?.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          align='center'
                        >
                          {'No record found'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      data?.map((country) => (
                        <TableRow key={country.id}>
                          <TableCell align='center'>
                            {country?.labelEn}
                          </TableCell>
                          <TableCell align='center'>
                            {country?.labelAr}
                          </TableCell>
                          <TableCell align='center'>
                            {' '}
                            <span
                              dangerouslySetInnerHTML={{
                                __html: country.emoji,
                              }}
                            />
                          </TableCell>
                          <TableCell align='center'>
                            {country?.position}
                          </TableCell>
                          <TableCell align='center'>
                            <Label
                              variant='ghost'
                              color={country.activeState ? 'success' : 'error'}
                            >
                              {country.activeState ? 'Active' : 'InActive'}
                            </Label>
                          </TableCell>
                          <TableCell align='center'>
                            <IconButton
                              aria-label='edit'
                              onClick={() => handleUpdate(country)}
                            >
                              <Iconify icon={'eva:edit-fill'} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>
          </Card>
          <AddOnCategoryFormModal
            touched={touched}
            errors={errors}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            onClose={onClose}
            resetForm={resetForm}
            setFieldValue={setFieldValue}
            toggle={toggle}
            values={values}
          />
        </Container>
      </Page>
    </>
  );
};

export default Index;
